import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import DocViewer, { DocViewerRenderers } from '@cyntler/react-doc-viewer';
import '@cyntler/react-doc-viewer/dist/index.css';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import * as mammoth from 'mammoth';
import * as XLSX from 'xlsx';
import { ExternalLink, FileWarning, Sheet as SheetIcon, FileText } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { storageService } from '@/services';
import { useSourceExportPreview } from '@/hooks/use-sources';
import { sanitizeDocumentHtml } from '@/lib/sanitize';
import type { Source } from '@/types';
import { getFileExtension } from '../types';

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

const DOC_VIEWER_CONFIG = { header: { disableFileName: true } };
const SPREADSHEET_EXTENSIONS = new Set(['xlsx', 'xls']);
const NO_CLIENT_PREVIEW_EXTENSIONS = new Set(['doc', 'ppt', 'pptx']);

/** Preview shell shared by every doc-viewer variant. No border/card chrome of its own — the
 *  page embedding this (source-detail.tsx) already has its own header showing the file name, so
 *  wrapping the preview in a second bordered "card" with a duplicate name/icon just nested two
 *  boxes inside each other. Fills the full height/width its parent gives it edge-to-edge, and only
 *  adds a small floating "open in new tab" affordance when there's somewhere else to open the
 *  file. */
function DocumentFrame({
  openUrl,
  children
}: {
  name: string;
  icon?: typeof FileText;
  openUrl?: string;
  padded?: boolean;
  children: ReactNode;
}) {
  return (
    <div className='relative flex flex-col h-full overflow-hidden'>
      {openUrl && (
        <Button variant='secondary' size='icon-sm' className='absolute top-3 right-3 z-10 shadow-sm' asChild>
          <a href={openUrl} target='_blank' rel='noopener noreferrer' title='Open in new tab'>
            <ExternalLink className='size-3.5' />
          </a>
        </Button>
      )}
      <div className='flex-1 min-h-0'>{children}</div>
    </div>
  );
}

/** Centered placeholder for a preview that failed to generate — replaces the bare
 *  "broken image" the browser shows for a blocked/failed iframe with something legible. */
function PreviewErrorState({ message }: { message: string }) {
  return (
    <div className='flex h-full flex-col items-center justify-center gap-2 text-center px-6'>
      <FileWarning className='size-8 text-muted-foreground/50' />
      <p className='text-sm text-muted-foreground max-w-sm'>{message}</p>
    </div>
  );
}

/** Fetches raw bytes from a (possibly presigned, third-party-hosted) URL — necessary because
 *  uploaded files are served as `application/octet-stream` (no Content-Type was signed at upload
 *  time), so a renderer pointed straight at the presigned URL treats it as an opaque download
 *  rather than a file to render. Also exposes a same-origin `blob:` URL purely for the "open in a
 *  new tab" link — that's a navigation, not a `fetch`, so it isn't subject to the page's CSP
 *  `connect-src` (which intentionally does not allowlist `blob:`, since nothing should need to
 *  `fetch()` one — see `PdfPages`, which consumes the bytes directly instead). */
function useFileBytes(fetchUrl: () => Promise<string>, mimeType: string, deps: unknown[]) {
  const [bytes, setBytes] = useState<ArrayBuffer | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    setBytes(null);
    setBlobUrl(null);
    setError(false);
    (async () => {
      try {
        const url = await fetchUrl();
        const response = await fetch(url);
        if (!response.ok) throw new Error(`fetch failed: ${response.status}`);
        const buffer = await response.arrayBuffer();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(new Blob([buffer], { type: mimeType }));
        setBytes(buffer);
        setBlobUrl(objectUrl);
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { bytes, blobUrl, error };
}

/** Renders every page of a PDF as its own centered, shadowed "sheet of paper" scaled to fit the
 *  container's width — the look of a native doc preview (Drive, Notion) rather than the browser's
 *  own PDF plugin chrome (dark toolbar + thumbnail rail) that clashes with the app's theme and
 *  letterboxes the page inside a fixed-width frame regardless of the panel's actual size. */
function PdfPages({ fileBytes, name }: { fileBytes: ArrayBuffer; name: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [pageWidth, setPageWidth] = useState(0);
  const [numPages, setNumPages] = useState(0);
  const [loadError, setLoadError] = useState(false);
  const file = useMemo(() => ({ data: fileBytes }), [fileBytes]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => setPageWidth(entry.contentRect.width));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  if (loadError) {
    return <PreviewErrorState message="Couldn't render this PDF. Try opening it in a new tab instead." />;
  }

  return (
    <div ref={containerRef} className='h-full w-full overflow-auto'>
      <Document
        file={file}
        onLoadSuccess={({ numPages: n }) => setNumPages(n)}
        onLoadError={() => setLoadError(true)}
        loading={<Skeleton className='h-full w-full rounded-none' />}
        className='flex flex-col items-center gap-4 py-4 sm:py-6'
      >
        {pageWidth > 0 &&
          Array.from({ length: numPages }, (_, i) => (
            <Page
              key={i}
              pageNumber={i + 1}
              width={pageWidth}
              className='overflow-hidden [&>canvas]:!h-auto [&>canvas]:!w-full'
              loading={null}
              aria-label={`${name} — page ${i + 1}`}
            />
          ))}
      </Document>
    </div>
  );
}

/** Renders a PDF (uploaded or Google-exported) by feeding its raw bytes to react-pdf directly —
 *  see `useFileBytes`. Fetching the bytes ourselves (rather than pointing react-pdf at the
 *  presigned URL directly) sidesteps this bucket's ranged-request headers, which pdfjs otherwise
 *  chokes on. */
function PdfViewer({ name, fetchUrl, deps }: { name: string; fetchUrl: () => Promise<string>; deps: unknown[] }) {
  const { bytes, blobUrl, error } = useFileBytes(fetchUrl, 'application/pdf', deps);

  if (error) {
    return (
      <DocumentFrame name={name}>
        <PreviewErrorState message="Couldn't load this PDF. Try opening it in a new tab instead." />
      </DocumentFrame>
    );
  }

  if (!bytes) {
    return (
      <DocumentFrame name={name}>
        <Skeleton className='h-full w-full rounded-none' />
      </DocumentFrame>
    );
  }

  return (
    <DocumentFrame name={name} openUrl={blobUrl ?? undefined}>
      <PdfPages fileBytes={bytes} name={name} />
    </DocumentFrame>
  );
}

/** Renders a Word document by converting it to HTML client-side with mammoth, entirely from
 *  bytes we already have — no external viewer involved. This matters because the natural
 *  alternative (embedding Microsoft's Office Online Viewer, which is what
 *  `@cyntler/react-doc-viewer`'s own MSDoc renderer does under the hood) requires *Microsoft's
 *  servers* to fetch and positively identify the file from its URL alone: our uploads are served
 *  from S3 as extension-less keys with an `application/octet-stream` Content-Type, so the viewer
 *  can't tell what it's looking at and fails with a generic "File not found". mammoth sidesteps
 *  all of that by reading the bytes directly. (It only understands modern `.docx`, not the legacy
 *  binary `.doc` format — those fall back to a plain "no preview" state.) */
function DocxViewer({ name, fetchUrl, deps }: { name: string; fetchUrl: () => Promise<string>; deps: unknown[] }) {
  const { bytes, blobUrl, error: fetchError } = useFileBytes(fetchUrl, 'application/octet-stream', deps);
  const [html, setHtml] = useState<string | null>(null);
  const [convertError, setConvertError] = useState(false);

  useEffect(() => {
    if (!bytes) return;
    let cancelled = false;
    setHtml(null);
    setConvertError(false);
    mammoth
      .convertToHtml({ arrayBuffer: bytes })
      .then((result) => {
        if (!cancelled) setHtml(sanitizeDocumentHtml(result.value));
      })
      .catch(() => {
        if (!cancelled) setConvertError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [bytes]);

  if (fetchError || convertError) {
    return (
      <DocumentFrame name={name}>
        <PreviewErrorState message="Couldn't render this document. Try opening it in a new tab instead." />
      </DocumentFrame>
    );
  }

  if (!html) {
    return (
      <DocumentFrame name={name}>
        <Skeleton className='h-full w-full rounded-none' />
      </DocumentFrame>
    );
  }

  return (
    <DocumentFrame name={name} openUrl={blobUrl ?? undefined} padded>
      <div className='h-full w-full overflow-auto p-8 sm:p-12'>
        <div
          className='prose prose-sm sm:prose-base max-w-none prose-table:border-collapse [&_td]:border [&_td]:border-border [&_td]:p-2 [&_th]:border [&_th]:border-border [&_th]:p-2'
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </div>
    </DocumentFrame>
  );
}

/** Renders a spreadsheet by parsing it client-side with SheetJS and printing each sheet as an
 *  HTML table — see `DocxViewer` for why we don't hand this to an external viewer instead. SheetJS
 *  (unlike mammoth) understands both the legacy binary `.xls` format and modern `.xlsx`. */
function XlsxViewer({ name, fetchUrl, deps }: { name: string; fetchUrl: () => Promise<string>; deps: unknown[] }) {
  const { bytes, blobUrl, error } = useFileBytes(fetchUrl, 'application/octet-stream', deps);
  const [sheetIndex, setSheetIndex] = useState(0);

  const workbook = useMemo(() => {
    if (!bytes) return null;
    try {
      return XLSX.read(bytes, { type: 'array' });
    } catch {
      return undefined;
    }
  }, [bytes]);

  if (error || workbook === undefined) {
    return (
      <DocumentFrame name={name} icon={SheetIcon}>
        <PreviewErrorState message="Couldn't read this spreadsheet. Try opening it in a new tab instead." />
      </DocumentFrame>
    );
  }

  if (!workbook) {
    return (
      <DocumentFrame name={name} icon={SheetIcon}>
        <Skeleton className='h-full w-full rounded-none' />
      </DocumentFrame>
    );
  }

  const sheetNames = workbook.SheetNames;
  const activeSheet = sheetNames[Math.min(sheetIndex, sheetNames.length - 1)];
  const html = sanitizeDocumentHtml(
    XLSX.utils.sheet_to_html(workbook.Sheets[activeSheet], { header: '', footer: '', id: 'sheet-preview' })
  );

  return (
    <DocumentFrame name={name} icon={SheetIcon} openUrl={blobUrl ?? undefined} padded>
      <div className='flex h-full w-full flex-col'>
        {sheetNames.length > 1 && (
          <div className='flex shrink-0 gap-1 overflow-x-auto border-b px-3 py-1.5'>
            {sheetNames.map((sheetName, i) => (
              <button
                key={sheetName}
                type='button'
                onClick={() => setSheetIndex(i)}
                className={`shrink-0 rounded px-2.5 py-1 text-xs font-medium transition-colors ${
                  i === sheetIndex
                    ? 'bg-muted text-foreground'
                    : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
                }`}
              >
                {sheetName}
              </button>
            ))}
          </div>
        )}
        <div
          className='min-h-0 flex-1 overflow-auto p-2 sm:p-4 [&_table]:border-collapse [&_table]:text-sm [&_td]:border [&_td]:border-border [&_td]:px-2 [&_td]:py-1 [&_th]:border [&_th]:border-border [&_th]:bg-muted/50 [&_th]:px-2 [&_th]:py-1 [&_th]:font-medium'
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </div>
    </DocumentFrame>
  );
}

/** Uploaded FILE sources — rendered from a presigned S3 URL. */
export function SourceFileViewer({ source }: { source: Source }) {
  const [uri, setUri] = useState<string | null>(null);
  const extension = getFileExtension(source);
  const isSpreadsheet = SPREADSHEET_EXTENSIONS.has(extension);
  const skipGenericViewer = extension === 'pdf' || extension === 'docx' || isSpreadsheet;

  useEffect(() => {
    if (skipGenericViewer) return;
    let cancelled = false;
    setUri(null);
    storageService.generateDownloadUrl(source.value).then((result) => {
      if (!cancelled) setUri(result.url);
    });
    return () => {
      cancelled = true;
    };
  }, [source.value, skipGenericViewer]);

  // DocViewer's internal reducer keys off this prop by reference — a new array/object
  // literal on every render sends it into a repeated dispatch loop ("Maximum update depth
  // exceeded"), so it must stay referentially stable across re-renders.
  const documents = useMemo(() => (uri ? [{ uri, fileType: extension }] : []), [uri, extension]);

  if (extension === 'pdf') {
    return (
      <PdfViewer
        name={source.name}
        fetchUrl={async () => (await storageService.generateDownloadUrl(source.value)).url}
        deps={[source.value]}
      />
    );
  }

  if (extension === 'docx') {
    return (
      <DocxViewer
        name={source.name}
        fetchUrl={async () => (await storageService.generateDownloadUrl(source.value)).url}
        deps={[source.value]}
      />
    );
  }

  if (isSpreadsheet) {
    return (
      <XlsxViewer
        name={source.name}
        fetchUrl={async () => (await storageService.generateDownloadUrl(source.value)).url}
        deps={[source.value]}
      />
    );
  }

  if (!uri) {
    return (
      <DocumentFrame name={source.name}>
        <Skeleton className='h-full w-full rounded-none' />
      </DocumentFrame>
    );
  }

  if (NO_CLIENT_PREVIEW_EXTENSIONS.has(extension)) {
    return (
      <DocumentFrame name={source.name} openUrl={uri}>
        <PreviewErrorState message="No preview is available for this file type yet. Try opening it in a new tab instead." />
      </DocumentFrame>
    );
  }

  return (
    <DocumentFrame name={source.name} openUrl={uri} padded>
      <DocViewer
        documents={documents}
        pluginRenderers={DocViewerRenderers}
        style={{ height: '100%' }}
        config={DOC_VIEWER_CONFIG}
      />
    </DocumentFrame>
  );
}

/** Google Docs/Sheets (INTEGRATION) — server-exported to PDF/XLSX via kim-tool-server, since
 *  these are usually shared with the bot's service account rather than the viewer's own Google
 *  account, so a client-side Google embed would just show a login wall. Doc exports render as
 *  PDF and go through the Blob-URL path (`PdfViewer`/`useBlobUrl`) since the exported file is
 *  hosted on our S3 bucket, not a CSP-allowlisted domain. */
export function SourceGoogleViewer({ source }: { source: Source }) {
  const { data, isLoading, isError } = useSourceExportPreview(source.id, true);
  const isSheet = data?.mimeType?.includes('spreadsheet') ?? false;
  const icon = isSheet ? SheetIcon : FileText;

  if (isLoading || !data) {
    return (
      <DocumentFrame name={source.name} icon={icon}>
        <Skeleton className='h-full w-full rounded-none' />
      </DocumentFrame>
    );
  }

  if (isError) {
    return (
      <DocumentFrame name={source.name} icon={icon}>
        <PreviewErrorState message="Couldn't generate a preview for this source." />
      </DocumentFrame>
    );
  }

  if (isSheet) {
    return <XlsxViewer name={source.name} fetchUrl={async () => data.previewUrl} deps={[data.previewUrl]} />;
  }

  return <PdfViewer name={source.name} fetchUrl={async () => data.previewUrl} deps={[data.previewUrl]} />;
}
